/**
 * `useAuth` — the SPA auth lifecycle hook (E10-F01-T05).
 *
 * This is the consumer that wires the pure auth model + transport + session
 * store into the running app — the React-side counterpart of the legacy
 * `auth.js init()` + `core.js handleUnauthorized()`:
 *
 *   - on mount it runs the cold-start restore (`runSessionRestore`): read the
 *     persisted token + cached user, confirm with `GET /auth/me`, and on success
 *     warm the clients + projects lists, then navigate to the last saved route;
 *   - it registers the single app-level `handleUnauthorized` reaction so ANY 401 /
 *     `error:'unauthorized'` (detected once in the api-client) flips the UI to the
 *     login view and hides the main app;
 *   - it exposes `login` (POST /auth/login → persist session → warm data) and
 *     `logout` (best-effort POST /auth/logout → clear the SAME local keys).
 *
 * Side effects (fetch, query prefetch, navigate, storage) live here; the
 * *decisions* live in the pure `./model.ts`, so this hook stays a thin wiring
 * layer and the restore branches stay unit-testable without a DOM (see
 * `./model.test.ts` + `./unauthorized.test.ts`, both tagged `@id:E10-F01-T05`).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ApiResult, AuthIdentity } from '@eer/shared';
import { qk } from '../api/queryKeys.js';
import { apiClient } from '../api/client.js';
import {
  getCachedUser,
  getSessionToken,
  setCachedUser,
  setSession,
  clearSession,
} from '../api/session.js';
import { loadRoute } from '../router/storage.js';
import type { Route } from '../router/routes.js';
import { fetchMe, login as loginRequest, logoutRequest, type LoginCredentials } from './api.js';
import { runSessionRestore } from './model.js';
import {
  registerPasswordResetRequiredHandler,
  registerUnauthorizedHandler,
} from './unauthorized.js';

/** Public auth state + actions exposed to the app (App.tsx gates on `status`). */
export interface AuthState {
  /**
   * `restoring` — cold-start session check in flight (render nothing/splash);
   * `authenticated` — a confirmed user; render the shell;
   * `anonymous` — show the login view.
   */
  readonly status: 'restoring' | 'authenticated' | 'anonymous';
  /** The authenticated principal, or null when anonymous/restoring-without-cache. */
  readonly user: AuthIdentity | null;
  /** True while a `login` request is in flight (disables the submit button). */
  readonly loggingIn: boolean;
  /** Last login error message (shown under the form), or null. */
  readonly loginError: string | null;
  /** Authenticate with email + password; resolves to true on success. */
  readonly login: (credentials: LoginCredentials) => Promise<boolean>;
  /** Best-effort server logout + local clear; always flips to anonymous. */
  readonly logout: () => Promise<void>;
  /**
   * E12-F08-T13: set when the API answered 403 password_reset_required — the
   * session is already dropped; the host opens the reset view pre-filled with
   * `email`. `seq` grows on every event so the host can react to each one once.
   */
  readonly resetRequest: { readonly seq: number; readonly email: string | null } | null;
}

export interface UseAuthOptions {
  /** Navigate callback (the shared router instance from `useRouter`). */
  readonly navigate: (route: Route) => void;
}

export function useAuth({ navigate }: UseAuthOptions): AuthState {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AuthState['status']>('restoring');
  const [user, setUser] = useState<AuthIdentity | null>(null);
  const [loggingIn, setLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  // Keep the latest `navigate` reachable from the mount-only restore effect
  // without re-running the restore when the callback identity changes.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  /**
   * Warm the clients + projects lists (acceptance: "грузятся clients и projects").
   * Prefetch through TanStack Query so the lists are already cached when their
   * views mount; the api-client never throws, so a failed prefetch only leaves an
   * un-warmed cache (the view's own query refetches) and never breaks restore.
   */
  const loadInitialData = useCallback(async (): Promise<void> => {
    const fetchList = async (domain: 'clients' | 'projects', path: string): Promise<void> => {
      await queryClient.prefetchQuery({
        queryKey: qk[domain].list(),
        queryFn: async ({ signal }) => {
          const res = await apiClient.get<unknown>(path, { signal });
          // Throw on failure so TanStack marks it errored (the view refetches);
          // restore itself swallows this via Promise.allSettled below.
          if (!res.ok) throw new Error(res.error.message);
          return res.data;
        },
      });
    };
    await Promise.allSettled([
      fetchList('clients', '/clients'),
      fetchList('projects', '/projects'),
    ]);
  }, [queryClient]);

  // Register the single app-level unauthorized reaction: clear local state (the
  // base handler already did the storage clear) and flip the UI to login. Fired
  // by the api-client on ANY 401 / error:'unauthorized'.
  useEffect(() => {
    const unsubscribe = registerUnauthorizedHandler(() => {
      setUser(null);
      setStatus('anonymous');
    });
    return unsubscribe;
  }, []);

  // E12-F08-T13: the reset-gate reaction (403 password_reset_required, detected
  // once in the api-client, which already dropped the session) — wipe cached
  // data and flip to anonymous with a reset request for the host (App.tsx).
  const [resetRequest, setResetRequest] = useState<AuthState['resetRequest']>(null);
  useEffect(() => {
    return registerPasswordResetRequiredHandler((email) => {
      queryClient.clear();
      setUser(null);
      setLoginError(null);
      setStatus('anonymous');
      setResetRequest((prev) => ({ seq: (prev?.seq ?? 0) + 1, email }));
    });
  }, [queryClient]);

  // Cold-start restore — runs once on mount (parity with legacy `auth.js init()`).
  useEffect(() => {
    let cancelled = false;
    void runSessionRestore({
      token: getSessionToken(),
      cachedUser: getCachedUser(),
      fetchMe: async () => {
        const res = await fetchMe();
        return res.ok ? res.data : null;
      },
      cacheUser: setCachedUser,
      loadInitialData,
      savedRoute: loadRoute(),
      // The path this cold load started on — an explicit deep-link URL must win
      // over the stale persisted `savedRoute` during restore (PK-QA-13). Guarded
      // for non-browser/test environments the same way the router does.
      currentPath: typeof window !== 'undefined' ? window.location.pathname : '/',
      navigate: (route) => navigateRef.current(route),
      clearSession,
    }).then((result) => {
      if (cancelled) return;
      // The warm-up hit the reset gate (403) mid-restore: the session is gone and
      // the reset reaction already flipped to anonymous — do not overwrite it.
      if (result.status === 'authenticated' && !getSessionToken()) return;
      setUser(result.user);
      setStatus(result.status);
    });
    return () => {
      cancelled = true;
    };
  }, [loadInitialData]);

  const login = useCallback(
    async (credentials: LoginCredentials): Promise<boolean> => {
      setLoggingIn(true);
      setLoginError(null);
      let res: ApiResult<{ session_token: string; user: AuthIdentity }>;
      try {
        res = await loginRequest(credentials);
      } finally {
        setLoggingIn(false);
      }
      if (!res.ok) {
        setLoginError(res.error.message);
        return false;
      }
      setSession(res.data.session_token, res.data.user);
      setUser(res.data.user);
      await loadInitialData();
      // Reset gate (E12-F08-T13): the warm-up got 403 password_reset_required,
      // the session was dropped and the reset view is already showing.
      if (!getSessionToken()) return false;
      setStatus('authenticated');
      return true;
    },
    [loadInitialData],
  );

  const logout = useCallback(async (): Promise<void> => {
    // Best-effort server logout; local clear happens regardless (acceptance:
    // "logout делает best-effort серверный logout и локально чистит те же ключи").
    await logoutRequest();
    clearSession();
    queryClient.clear();
    setUser(null);
    setLoginError(null);
    setStatus('anonymous');
  }, [queryClient]);

  return { status, user, loggingIn, loginError, login, logout, resetRequest };
}
